import React, {useState, useEffect} from "react";
import withLayout from "../../hocs/Layout";
import {getMerchant} from '../../helpers/ReduxHelpers';
import {apiConstants, postGeneric} from '../../common/constants';
import {useForm} from "react-hook-form";
import RateList from "./rateList";
import {Box, Typography, InputAdornment, Divider, ToggleButtonGroup, ToggleButton, IconButton, Popover, Alert} from "@mui/material";
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {FormProvider, RHFRadioGroup, RHFTextField} from "../../common/hook-form";
import {LoadingButton} from "@mui/lab";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from 'yup'
import PageTitle from "../../common/PageTitle";
import {FONT, SIZE, WEIGHT} from '../../styles/foundation/typography';
import {NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE, LIGHT_BG} from '../../styles/foundation/colors';
import {SHADOW} from '../../styles/foundation/effects';
import {pageContainerSx} from '../../styles/patterns';

const cardSx = {bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '16px', boxShadow: SHADOW.card, p: {xs: 2, md: 2.5}};
const fieldLabelSx = {display: 'flex', alignItems: 'center', gap: '5px', fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.bold, color: SUBTLE, textTransform: 'uppercase', letterSpacing: '0.03em', mb: 0.75, '& svg': {fontSize: 13, opacity: 0.7}};
// Cluster: a subtly tinted container that visually binds a group of related fields (Route,
// Package) so they read as one decision, not several loose boxes next to each other.
const clusterSx = {display: 'flex', alignItems: 'flex-end', gap: 1.5, flexWrap: 'wrap', bgcolor: LIGHT_BG, border: `1px solid ${BORDER}`, borderRadius: '12px', p: 1.5};
const unitAdornmentSx = {fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.medium, color: SUBTLE};
const toggleGroupSx = {
    '& .MuiToggleButton-root': {
        fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.semibold, textTransform: 'none',
        px: 2.5, py: 0.85, borderColor: BORDER, color: NAVY,
        '&.Mui-selected': {bgcolor: PRIMARY_BLUE, color: WHITE, '&:hover': {bgcolor: PRIMARY_BLUE}},
        '&:hover': {bgcolor: LIGHT_BG},
    },
};

// B2C weight cap per spec (70 kg). PTL is uncapped (see shipment-calculator-ptl).
const MAX_WEIGHT_B2C = 70000;

const ShipmentRateCalculator = () => {
    const merchant = getMerchant();
    const merchantId = merchant.id ? merchant.id : ''

    const [isLoading, setLoading] = useState(false);
    const [rateListing, setRateList] = useState(null);   // forward / base results
    const [rtoListing, setRtoList] = useState(null);     // RTO results
    const [withQC, setWithQC] = useState(false);         // QC toggle (Change 2)
    const [tipAnchor, setTipAnchor] = useState(null);

    const buildPayload = (data, extra) => Object.assign(
        {},
        data,
        // Scope results to this merchant's own rule-mapped/allocated couriers — every other
        // caller of this API (order creation, reattempt, clone, returns) already sends this;
        // without it the backend returns pricing across the full courier universe, unfiltered.
        {isMerchant: true, merchantOid: merchantId, onlyCouriersAsPerRuleMapping: true, checkServiceable: true},
        {codAmount: (data.orderType === 'FORWARD' && data.paymentType === 'COD') ? data.codAmount : 0},
        extra || {}
    )

    const onSubmit = (data) => {
        setLoading(true);
        setRateList([]);
        setRtoList(null);

        const isForward = data.orderType === 'FORWARD';
        // base call: RVP passes withQC; FORWARD is the plain forward call
        const basePayload = buildPayload(data, isForward ? {} : {withQC});
        const calls = [postGeneric(apiConstants.SHIPMENT_RATE_CALCULATOR, basePayload)];
        // Always fetch RTO pricing alongside forward rates
        if (isForward) calls.push(postGeneric(apiConstants.SHIPMENT_RATE_CALCULATOR, buildPayload(data, {isRTO: true})));

        Promise.allSettled(calls).then(([base, rto]) => {
            setRateList(base.status === 'fulfilled' ? (base.value?.data || []) : []);
            if (isForward) setRtoList(rto?.status === 'fulfilled' ? (rto.value?.data || []) : null);
        }).finally(() => setLoading(false));
    }

    const methods = useForm({
        resolver: yupResolver(
            Yup.object().shape({
                orderType: Yup.string().oneOf(['FORWARD', 'RVP']).required("Please select a shipment type"),
                paymentType: Yup.string().oneOf(['PREPAID', 'COD']).required("Please select a payment type"),
                from: Yup.string().required("Origin pincode is required!").matches(/^\d{6}$/, 'Origin pincode must be 6 digits'),
                to: Yup.string().required("Destination pincode is required!").matches(/^\d{6}$/, 'Destination pincode must be 6 digits'),
                length: Yup.number().typeError('Shipment length must be valid').positive('Length must be greater than 0').required("Shipment length is required!"),
                breadth: Yup.number().typeError('Shipment breadth must be valid').positive('Breadth must be greater than 0').required("Shipment breadth is required!"),
                height: Yup.number().typeError('Shipment height must be valid').positive('Height must be greater than 0').required("Shipment height is required!"),
                weight: Yup.number().typeError('Shipment weight must be valid').positive('Weight must be greater than 0')
                    .max(MAX_WEIGHT_B2C, 'Weight must be between 1g and 70,000g').required("Shipment weight is required!"),
                // Change 4/5: COD amount required only for FORWARD + COD
                codAmount: Yup.number().when(['orderType', 'paymentType'], {
                    is: (orderType, paymentType) => orderType === 'FORWARD' && paymentType === 'COD',
                    then: (s) => s.typeError('COD Amount must be valid').positive('COD amount must be greater than 0 for COD orders').required('COD amount must be greater than 0 for COD orders'),
                    otherwise: (s) => s.nullable().transform((v, o) => (o === '' ? null : v)),
                }),
            })
        ),
        defaultValues: {
            paymentType: 'PREPAID', orderType: 'FORWARD',
            from: '', to: '', length: '', breadth: '', height: '', weight: '', codAmount: '',
        }
    })
    const {handleSubmit: handleSubmitForm, watch, setValue} = methods
    const orderType = watch('orderType')
    const isForward = orderType === 'FORWARD'

    // Change 5: when switching to RVP, COD isn't valid — reset to PREPAID and clear amount.
    useEffect(() => {
        if (orderType === 'RVP') {
            setValue('paymentType', 'PREPAID')
            setValue('codAmount', '')
        } else {
            setWithQC(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderType])

    const paymentOptions = isForward
        ? [{label: 'Prepaid', value: 'PREPAID'}, {label: 'COD', value: 'COD'}]
        : [{label: 'Prepaid', value: 'PREPAID'}]   // Change 5: hide COD on RVP

    return (
        <Box sx={pageContainerSx}>
            <PageTitle subTitle="Estimate shipping rates across courier partners for a parcel — enter the route, package and value.">D2C Rate Calculator</PageTitle>

            {/* Full-width filter bar — every input in one clear strip, grouped and spaced so
               nothing ever collapses below its working size. Results (below) get the whole
               page width instead of being squeezed into a narrower column next to a form. */}
            <Box sx={cardSx}>
                <FormProvider methods={methods} onSubmit={handleSubmitForm(onSubmit)}>
                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 3}}>
                        <RHFRadioGroup color="primary" label="Shipment Type *" name="orderType" options={[{label: 'Forward', value: 'FORWARD'}, {label: 'Reverse', value: 'RVP'}]} />
                        <RHFRadioGroup color="primary" label="Payment Mode *" name="paymentType" options={paymentOptions} />
                    </Box>

                    <Divider sx={{my: 1.5}} />

                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, mb: 1}}>
                        <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: NAVY}}>
                            Shipment Details
                        </Typography>
                        <IconButton size="small" onClick={(e) => setTipAnchor(e.currentTarget)}>
                            <InfoOutlinedIcon sx={{fontSize: 16, color: PRIMARY_BLUE}} />
                        </IconButton>
                        <Popover
                            open={Boolean(tipAnchor)}
                            anchorEl={tipAnchor}
                            onClose={() => setTipAnchor(null)}
                            anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                            transformOrigin={{vertical: 'top', horizontal: 'left'}}
                            slotProps={{paper: {sx: {maxWidth: 360, borderRadius: '10px', overflow: 'hidden'}}}}
                        >
                            <Alert severity="info" sx={{fontFamily: FONT, fontSize: SIZE['2xs']}}>
                                <strong>Tip:</strong> Enter pickup and delivery pincodes, package dimensions and weight to compare live rates
                                across every courier mapped to your account. Forward rates and RTO (return) costs are shown side by side for each courier.
                            </Alert>
                        </Popover>
                    </Box>

                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1.5}}>
                        {/* Route cluster */}
                        <Box sx={clusterSx}>
                            <Box sx={{flex: '1 1 160px', minWidth: 160}}>
                                <RHFTextField color="primary" name="from" label="Pickup Pincode" mandatory size="small" fullWidth
                                    sx={{bgcolor: WHITE, borderRadius: '8px'}}
                                    InputProps={{sx: {fontSize: 12}, startAdornment: <InputAdornment position="start"><LocationOnOutlinedIcon sx={{fontSize: 16, color: SUBTLE}} /></InputAdornment>}} />
                            </Box>
                            <Box sx={{flex: '1 1 160px', minWidth: 160}}>
                                <RHFTextField color="primary" name="to" label="Delivery Pincode" mandatory size="small" fullWidth
                                    sx={{bgcolor: WHITE, borderRadius: '8px'}}
                                    InputProps={{sx: {fontSize: 12}, startAdornment: <InputAdornment position="start"><LocationOnOutlinedIcon sx={{fontSize: 16, color: SUBTLE}} /></InputAdornment>}} />
                            </Box>
                        </Box>

                        {/* Package cluster */}
                        <Box sx={clusterSx}>
                            <Box sx={{width: 105}}>
                                <RHFTextField color="primary" name="length" label="Length" mandatory size="small" fullWidth sx={{bgcolor: WHITE, borderRadius: '8px'}} InputProps={{sx: {fontSize: 12}, endAdornment: <InputAdornment position="end"><Typography sx={unitAdornmentSx}>cm</Typography></InputAdornment>}} />
                            </Box>
                            <Box sx={{width: 105}}>
                                <RHFTextField color="primary" name="breadth" label="Breadth" mandatory size="small" fullWidth sx={{bgcolor: WHITE, borderRadius: '8px'}} InputProps={{sx: {fontSize: 12}, endAdornment: <InputAdornment position="end"><Typography sx={unitAdornmentSx}>cm</Typography></InputAdornment>}} />
                            </Box>
                            <Box sx={{width: 105}}>
                                <RHFTextField color="primary" name="height" label="Height" mandatory size="small" fullWidth sx={{bgcolor: WHITE, borderRadius: '8px'}} InputProps={{sx: {fontSize: 12}, endAdornment: <InputAdornment position="end"><Typography sx={unitAdornmentSx}>cm</Typography></InputAdornment>}} />
                            </Box>
                            <Divider orientation="vertical" flexItem sx={{alignSelf: 'stretch', my: 0.5}} />
                            <Box sx={{width: 120}}>
                                <RHFTextField color="primary" name="weight" label="Weight" mandatory size="small" fullWidth
                                    sx={{bgcolor: WHITE, borderRadius: '8px'}}
                                    InputProps={{
                                        sx: {fontSize: 12},
                                        endAdornment: <InputAdornment position="end"><Typography sx={unitAdornmentSx}>gm</Typography></InputAdornment>,
                                    }} />
                            </Box>
                        </Box>

                        {/* Change 5: invoice/COD amount only for FORWARD + COD */}
                        {isForward && watch('paymentType') === 'COD' && (
                            <Box sx={clusterSx}>
                                <Box sx={{flex: '1 1 160px', minWidth: 160}}>
                                    <RHFTextField color="primary" name="codAmount" label="COD Amount" mandatory size="small" fullWidth
                                        sx={{bgcolor: WHITE, borderRadius: '8px'}}
                                        InputProps={{sx: {fontSize: 12}, startAdornment: <InputAdornment position="start"><Typography sx={unitAdornmentSx}>₹</Typography></InputAdornment>}} />
                                </Box>
                            </Box>
                        )}
                    </Box>

                    <Divider sx={{my: 1.5}} />

                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5}}>
                        <Box>
                            {/* Change 2: QC / Non-QC — RVP only (local state) */}
                            {!isForward && (
                                <Box>
                                    <Typography sx={fieldLabelSx}>QC Type</Typography>
                                    <ToggleButtonGroup exclusive size="small" value={withQC ? 'QC' : 'NON_QC'}
                                        onChange={(_e, v) => { if (v) setWithQC(v === 'QC') }} sx={toggleGroupSx}>
                                        <ToggleButton value="NON_QC">Non-QC</ToggleButton>
                                        <ToggleButton value="QC">QC</ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>
                            )}
                        </Box>
                        <LoadingButton loading={isLoading} type="submit" variant="contained" startIcon={<CalculateOutlinedIcon />}
                            sx={{
                                fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, textTransform: 'none',
                                borderRadius: '10px', bgcolor: PRIMARY_BLUE, py: 1.1, px: 3.5, width: {xs: '100%', sm: 'auto'},
                                boxShadow: `0 2px 10px ${PRIMARY_BLUE}4D`,
                                transition: 'box-shadow 0.15s ease, transform 0.15s ease',
                                '&:hover': {bgcolor: PRIMARY_BLUE, boxShadow: `0 4px 16px ${PRIMARY_BLUE}66`, transform: 'translateY(-1px)'},
                            }}>
                            Calculate Rates
                        </LoadingButton>
                    </Box>
                </FormProvider>
            </Box>

            <Box sx={{mt: 2}}>
                {rateListing
                    ? <RateList rateListing={rateListing} rtoListing={rtoListing} withQC={!isForward && withQC} orderType={orderType} />
                    : (
                        <Box sx={{...cardSx, minHeight: 180, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center'}}>
                            <Box sx={{width: 52, height: 52, borderRadius: '50%', bgcolor: LIGHT_BG, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5, color: SUBTLE, '& svg': {fontSize: 26}}}>
                                <LocalShippingOutlinedIcon />
                            </Box>
                            <Typography sx={{fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY}}>No rates yet</Typography>
                            <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.5, maxWidth: 320}}>
                                Fill in the shipment details above and hit Calculate to compare courier rates side by side.
                            </Typography>
                        </Box>
                    )}
            </Box>
        </Box>
    );
};

export default withLayout(ShipmentRateCalculator);
